import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

export type RecurrenceType = 'none' | 'daily' | 'weekdays' | 'weekly' | 'biweekly' | 'monthly';

const RECURRENCE_OPTIONS: { key: RecurrenceType; label: string; description: string }[] = [
  { key: 'none', label: 'None', description: 'One-time activity' },
  { key: 'daily', label: 'Daily', description: 'Every day' },
  { key: 'weekdays', label: 'Weekdays', description: 'Mon - Fri' },
  { key: 'weekly', label: 'Weekly', description: 'Once a week' },
  { key: 'biweekly', label: 'Biweekly', description: 'Every 2 weeks' },
  { key: 'monthly', label: 'Monthly', description: 'Once a month' },
];

interface RecurrencePickerProps {
  value: RecurrenceType;
  onChange: (value: RecurrenceType) => void;
  testIDPrefix?: string;
}

export default function RecurrencePicker({ value, onChange, testIDPrefix }: RecurrencePickerProps) {
  const styles = useThemedStyles(createStyles);

  return (
    <View style={styles.container} accessibilityRole="radiogroup" accessibilityLabel="Repeat frequency">
      {RECURRENCE_OPTIONS.map((opt) => (
        <TouchableOpacity
          key={opt.key}
          style={[styles.chip, value === opt.key && styles.chipActive]}
          onPress={() => onChange(opt.key)}
          accessibilityRole="button"
          accessibilityState={{ checked: value === opt.key }}
          accessibilityLabel={`${opt.label}, ${opt.description}`}
          testID={testIDPrefix ? `${testIDPrefix}.recurrence-${opt.key}` : `recurrence-${opt.key}`}
        >
          <Text style={[styles.chipLabel, value === opt.key && styles.chipLabelActive]} accessible={false}>
            {opt.label}
          </Text>
          <Text style={[styles.chipDescription, value === opt.key && styles.chipDescriptionActive]} accessible={false}>
            {opt.description}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 8 },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: theme.colors.bgTertiary,
    alignItems: 'center' as const,
    minWidth: 80,
  },
  chipActive: { backgroundColor: theme.colors.primary },
  chipLabel: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textSecondary },
  chipLabelActive: { color: theme.colors.onPrimary },
  chipDescription: { fontSize: 10, color: theme.colors.textDisabled, marginTop: 2 },
  chipDescriptionActive: { color: theme.colors.primaryLight },
});
